import React, { memo, useRef, useEffect } from 'react'

import {
  Icon,
  ChevronDownLine,
  AIGeneratedGradientFilled,
  Loader,
  CircleCheckGradientFilled,
  Tooltip,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { shape, string, bool, func, node, number } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

// Grace period before a hover-out closes the panel, so the pointer can cross the
// 1px seam between the trigger and the panel's hover bridge without it vanishing.
const HOVER_CLOSE_DELAY = 150

// Only "Create with AI" carries an icon today; keep the map so further iconed
// items (added to the backend `main_menu_items`) wire up without code changes.
const ICON_MAP = { ai: AIGeneratedGradientFilled }

// The "Create with AI" trigger swaps its leading glyph to mirror the live 3D-model
// generation status (passed only for that item): a spinner while a run is in flight,
// a check once one finishes (success or error - the outcome is distinguished inside
// the dropdown, not here). Idle falls back to the default ICON_MAP glyph.
const STATUS_ICONS = {
  generating: Loader,
  enhancing: Loader,
  succeeded: CircleCheckGradientFilled,
  failed: CircleCheckGradientFilled,
}
// Only the spinner needs a modifier: it drives the rotate animation and teal stroke.
// CircleCheckGradientFilled bakes its own gradient fill, so the completed states need none.
const STATUS_ICON_MODIFIERS = {
  generating: 'spinner',
  enhancing: 'spinner',
}
const ICON_SIZE = { width: '20', height: '20' }
const CHEVRON_SIZE = { width: '12', height: '12' }

function MenuItem({
  menuItem,
  isOpen = false,
  hoverEnabled = true,
  onToggle,
  onOpen = NOOP,
  onClose = NOOP,
  dropdown = null,
  hasPanel = false,
  persistPanel = false,
  generationStatus = undefined,
  generationTooltip = '',
  generationCount = 0,
}) {
  const { id, title, href, icon, hasDropdown = false, dataTrackName } = menuItem
  // A status icon (only supplied for the Create with AI trigger) takes precedence
  // over the item's static glyph so the trigger reflects generation progress.
  const statusIcon = generationStatus ? STATUS_ICONS[generationStatus] : null
  const statusIconModifier = generationStatus ? STATUS_ICON_MODIFIERS[generationStatus] : null
  const IconComponent = statusIcon || (icon ? ICON_MAP[icon] : null)
  // Only the spinner (generating / enhancing) names the in-flight run in a tooltip;
  // the idle / completed glyphs render bare.
  const isSpinner = statusIconModifier === 'spinner'
  // Sellers Hub opens a hover/focus panel; Menu flags it via `hasPanel` so the
  // panel wiring is active before the lazily-loaded `dropdown` content arrives.
  // The other `has_dropdown` triggers have no panel yet and keep a click toggle.
  // A truthy `dropdown` also implies a panel (backwards compatible for callers
  // that pass content directly).
  const isPanel = hasPanel || (hasDropdown && Boolean(dropdown))
  // Disclosure wiring: the trigger controls the panel by id (not an ARIA menu).
  const panelId = isPanel ? `${id}-panel` : undefined

  // Items without an href (Create with AI, Sellers Hub) are pure dropdown
  // triggers; items with an href (3D Models, 3D Printing) keep the link as a
  // fallback while still toggling. Hire a Freelancer has no dropdown and links out.
  const Wrapper = href ? 'a' : 'button'
  const wrapperProps = href ? { href } : { type: 'button' }
  const closeTimer = useRef(null)
  // Below the md breakpoint (< 960px) the stacked menu has no hover, so the spinner
  // tooltip (a hover-only affordance) is dropped entirely there rather than rendered.
  const isMobile = ['xs', 'sm'].includes(useBreakpoint())

  useEffect(() => () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
    }
  }, [])

  function cancelClose() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  function handleClick(event) {
    if (!hasDropdown) {
      return
    }

    // On a hover pointer (desktop) the panel opens on hover, so a click follows the
    // link: Create with AI -> the AI tool, 3D Models / 3D Printing -> their category
    // pages. Triggers with no href (Sellers Hub) have nowhere to go, so they toggle
    // the panel instead. Without a hover pointer (touch / stacked mobile menu) there
    // is no hover to reveal the panel, so every trigger toggles on click/tap.
    if (hoverEnabled && href) {
      return
    }

    event.preventDefault()
    onToggle(id)
  }

  function handleEnter() {
    cancelClose()
    onOpen(id)
  }

  function handleLeave() {
    cancelClose()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      // Pass the id so a stale timer can only close *this* item: when the pointer
      // crosses straight to a sibling trigger, the sibling opens first and this
      // delayed close must not wipe it (Menu guards on the still-active id).
      onClose(id)
    }, HOVER_CLOSE_DELAY)
  }

  function handleBlur(event) {
    // Close only when focus leaves the item entirely (not when moving into the panel).
    if (!event.currentTarget.contains(event.relatedTarget)) {
      cancelClose()
      onClose(id)
    }
  }

  // Desktop only: hover + focus open/close for the panel item (keyboard users
  // reach it via focus). On mobile the panel is click-toggled instead.
  const panelProps = isPanel && hoverEnabled
    ? {
      onMouseEnter: handleEnter,
      onMouseLeave: handleLeave,
      onFocus: handleEnter,
      onBlur: handleBlur,
    }
    : {}

  const statusGlyph = IconComponent && (
    <Icon
      notSpaced
      color="dark"
      size={ICON_SIZE}
      svgComponent={IconComponent}
      className={classnames(
        'unified-nav__menu-icon',
        statusIconModifier && `unified-nav__menu-icon--${statusIconModifier}`
      )}
    />
  )

  // Figma: the number of in-flight generations sits inside the spinner ring (the
  // ring rotates around it). The count is decorative - the tooltip and track text
  // already announce the in-flight state.
  const showCount = isSpinner && generationCount > 0
  const iconElement = showCount ? (
    <span className="unified-nav__menu-icon-wrap">
      {statusGlyph}
      <span className="unified-nav__generation-count" aria-hidden="true">{generationCount}</span>
    </span>
  ) : (
    statusGlyph
  )

  // Hover-to-open, desktop only. Below md (< 960px) the stacked menu has no hover, so
  // the tooltip is dropped and the bare spinner renders instead.
  const menuIcon = isSpinner && !isMobile ? (
    <Tooltip
      id="create-ai-generation-status"
      content={generationTooltip}
      place="bottom"
    >
      {iconElement}
    </Tooltip>
  ) : (
    iconElement
  )

  return (
    <li
      className={classnames(
        'unified-nav__menu-item',
        isOpen && 'is-open'
      )}
      {...panelProps}
    >
      <Wrapper
        {...wrapperProps}
        className={classnames(
          'unified-nav__menu-link',
          'cgt-button-text-md-semibold',
          IconComponent && 'unified-nav__menu-link--brand'
        )}
        data-track-name={dataTrackName}
        aria-expanded={hasDropdown ? isOpen : undefined}
        aria-controls={panelId}
        onClick={handleClick}
      >
        <span className="unified-nav__menu-content">
          {menuIcon}
          <span className="unified-nav__menu-label">{title}</span>
        </span>
        {hasDropdown && (
          <Icon
            notSpaced
            color="dark"
            size={CHEVRON_SIZE}
            svgComponent={ChevronDownLine}
            className="unified-nav__menu-chevron"
          />
        )}
      </Wrapper>
      {/* persistPanel (3D Models / 3D Printing) keeps the panel in the DOM always so
          its links are SSR'd and crawlable; CSS (.is-open) drives visibility. Other
          panels stay mount-on-open. */}
      {isPanel && (persistPanel || isOpen) && dropdown && React.cloneElement(dropdown, { id: panelId })}
    </li>
  )
}

MenuItem.propTypes = {
  menuItem: shape({
    id: string.isRequired,
    title: string.isRequired,
    href: string,
    icon: string,
    hasDropdown: bool,
    dataTrackName: string,
  }).isRequired,
  isOpen: bool,
  hoverEnabled: bool,
  onToggle: func.isRequired,
  onOpen: func,
  onClose: func,
  dropdown: node,
  hasPanel: bool,
  persistPanel: bool,
  generationStatus: string,
  generationTooltip: string,
  generationCount: number,
}

export default memo(MenuItem)
